import { useCallback, useState, useEffect, useRef } from 'react';
import { socketService } from '../services/trainingSocketService';
import { CodeViewerRequest, CodeViewerResponse, CodeViewerState } from '../types/training';

/**
 * Custom hook for managing code viewer state and WebSocket communication.
 *
 * Handles:
 * - Requesting training code from backend
 * - Managing loading/error states
 * - Listening for code responses
 */
export const useCodeViewer = () => {
  const listenersSetupRef = useRef(false);

  const [state, setState] = useState<CodeViewerState>({
    code: '',
    isLoading: false,
    error: null,
    modelType: 'transformer',
    configPath: 'config.json',
  });

  /**
   * Request training code from the backend.
   *
   * @param modelType - Optional model type ('cnn' or 'transformer')
   */
  const requestCode = useCallback(
    (modelType?: 'cnn' | 'transformer') => {
      console.log('[useCodeViewer] Requesting training code with model type:', modelType);
      setState((prev) => ({
        ...prev,
        isLoading: true,
        error: null,
      }));

      const request: CodeViewerRequest = {};
      if (modelType) {
        request.model_type = modelType;
      }

      console.log('[useCodeViewer] Emitting requestTrainingCode event with data:', request);
      socketService.emit('requestTrainingCode', request);
    },
    []
  );

  /**
   * Set up listener for code responses from backend.
   */
  useEffect(() => {
    // Prevent duplicate setup in Strict Mode
    if (listenersSetupRef.current) {
      return;
    }
    listenersSetupRef.current = true;
    console.log('[useCodeViewer] Setting up event listeners');

    const handleCodeResponse = (response: CodeViewerResponse) => {
      console.log('[useCodeViewer] Received trainingCodeResponse:', response);
      if (response.status === 'success') {
        console.log('[useCodeViewer] Code response successful, updating state');
        setState((prev) => ({
          ...prev,
          code: response.code || '',
          modelType: response.model_type || 'transformer',
          configPath: response.config_path || 'config.json',
          isLoading: false,
          error: null,
        }));
      } else {
        console.log('[useCodeViewer] Code response failed:', response.message);
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: response.message || 'Failed to generate code',
          code: '',
        }));
      }
    };

    const handleError = (errorData: any) => {
      console.log('[useCodeViewer] Received error event:', errorData);
      const message = typeof errorData === 'object' ? errorData?.message : String(errorData);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: message || 'An error occurred',
      }));
    };

    socketService.on('trainingCodeResponse', handleCodeResponse);
    socketService.on('error', handleError);

    return () => {
      listenersSetupRef.current = false;
      socketService.off('trainingCodeResponse', handleCodeResponse);
      socketService.off('error', handleError);
    };
  }, []);

  const copyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(state.code);
      return true;
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      return false;
    }
  }, [state.code]);

  const downloadCode = useCallback(() => {
    const element = document.createElement('a');
    const file = new Blob([state.code], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'train_model.py';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  }, [state.code]);

  return {
    ...state,
    requestCode,
    copyToClipboard,
    downloadCode,
  };
};
